import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Building2, Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ password: "", full_name: "", terms_accepted: false, privacy_accepted: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/orgs/invites/${token}`).then(({ data }) => {
      setData(data);
      setForm(f => ({ ...f, full_name: data.email?.split("@")[0] || "" }));
    }).catch(e => setError(e.response?.data?.detail || "Invitation not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (!form.terms_accepted || !form.privacy_accepted) return toast.error("Please accept the Terms and Privacy Policy");
    setSubmitting(true);
    try {
      await api.post("/orgs/invites/accept", { token, ...form });
      toast.success("Welcome to the team!");
      await refresh?.();
      navigate("/app/brand", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept invitation"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Frame><div className="text-center text-ink-muted py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></Frame>;
  if (error) return (
    <Frame>
      <div className="text-center">
        <h1 className="font-heading text-3xl uppercase tracking-tighter">Invitation unavailable</h1>
        <p className="text-ink-muted mt-3 text-sm">{error}</p>
        <Link to="/" className="btn-ghost-line inline-block mt-6 text-sm">Back to PITCHD</Link>
      </div>
    </Frame>
  );

  return (
    <Frame>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-bg-elev border border-line flex items-center justify-center">
          {data.organisation?.organisation_logo ? <img src={data.organisation.organisation_logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-volt" />}
        </div>
        <div>
          <div className="label-eyebrow">Invitation</div>
          <div className="font-heading text-2xl uppercase tracking-tight">Join {data.organisation?.organisation_name}</div>
        </div>
      </div>
      <p className="text-sm text-ink-muted mb-8">
        <span className="text-white">{data.inviter?.full_name || data.inviter?.email}</span> invited you to collaborate as <span className="text-volt uppercase tracking-widest text-xs">{data.role}</span>. Once you accept, you can work on shared campaigns, athlete outreach and shortlists.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
        <Field label="Email"><div className="text-sm py-2.5 px-3 border border-line bg-bg text-ink-muted" data-testid="invite-email-display">{data.email}</div></Field>
        <Field label="Full name">
          <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required data-testid="invite-fullname"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
        </Field>
        <Field label={data.user_exists ? "Set a password (replaces your current one)" : "Choose a password"}>
          <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8} data-testid="invite-password"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
        </Field>
        <label className="flex items-start gap-2 text-xs text-ink-muted">
          <input type="checkbox" checked={form.terms_accepted} onChange={e => setForm(f => ({ ...f, terms_accepted: e.target.checked }))} data-testid="invite-terms" />
          <span>I accept the <Link to="/terms" target="_blank" className="text-volt">Terms of Service</Link></span>
        </label>
        <label className="flex items-start gap-2 text-xs text-ink-muted">
          <input type="checkbox" checked={form.privacy_accepted} onChange={e => setForm(f => ({ ...f, privacy_accepted: e.target.checked }))} data-testid="invite-privacy" />
          <span>I accept the <Link to="/privacy" target="_blank" className="text-volt">Privacy Policy</Link></span>
        </label>
        <button type="submit" disabled={submitting} className="btn-volt w-full mt-4 disabled:opacity-40" data-testid="invite-accept-btn">
          {submitting ? "Joining…" : "Accept & join"}
        </button>
      </form>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-bg text-white flex flex-col">
      <header className="px-6 py-5 border-b border-line">
        <BrandLogo variant="wordmark" size={28} />
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="surface w-full max-w-md p-8">{children}</div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label-eyebrow block mb-2 text-[10px]">{label}</label>{children}</div>;
}
